<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { api } from "@/api";
import { parseCompId } from "@shared/comp";
import type { Team } from "@shared/types";
import PotReel from "@/components/picker/PotReel.vue";
import SpinButton from "@/components/picker/SpinButton.vue";
import ResultCard from "@/components/picker/ResultCard.vue";
import Toast from "@/components/common/Toast.vue";

type State = "IDLE" | "AWAITING_DRAW" | "SPINNING" | "RESULT" | "SAVING" | "SAVED" | "EXHAUSTED";

const state = ref<State>("IDLE");
const teams = ref<Team[]>([]);
const loadError = ref<string | null>(null);
const target = ref<{ pot1: string; pot2: string; pot3: string } | null>(null);
const spinTrigger = ref(0);
const savedName = ref<string>("");
const toast = ref<{ kind: "success" | "error"; message: string } | null>(null);

const route = useRoute();
const compId = parseCompId(route.query.comp);

onMounted(async () => {
  try {
    const res = await api.adminTeams();
    teams.value = res.teams;
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : String(e);
  }
});

const potTeams = (n: 1 | 2 | 3) => teams.value.filter(t => t.pot === n);
const teamById = (id: string | undefined) => id ? teams.value.find(t => t.id === id) ?? null : null;
const targetTeams = computed(() => {
  if (!target.value) return null;
  const p1 = teamById(target.value.pot1);
  const p2 = teamById(target.value.pot2);
  const p3 = teamById(target.value.pot3);
  if (!p1 || !p2 || !p3) return null;
  return { pot1: p1, pot2: p2, pot3: p3 };
});

const POT3_DURATION = 3.2;

async function spin() {
  if (state.value !== "IDLE") return;
  state.value = "AWAITING_DRAW";
  try {
    const res = await api.draw(compId);
    if ("error" in res) {
      state.value = "EXHAUSTED";
      return;
    }
    target.value = res.combo;
    state.value = "SPINNING";
    spinTrigger.value++;
    setTimeout(() => {
      if (state.value === "SPINNING") state.value = "RESULT";
    }, POT3_DURATION * 1000 + 100);
  } catch (e) {
    toast.value = { kind: "error", message: e instanceof Error ? e.message : "Draw failed" };
    state.value = "IDLE";
  }
}

async function save(name: string) {
  if (!target.value || state.value !== "RESULT") return;
  state.value = "SAVING";
  try {
    await api.createEntry({ name, ...target.value }, compId);
    savedName.value = name;
    state.value = "SAVED";
    toast.value = { kind: "success", message: "Entry saved" };
  } catch (e) {
    const err = e as { status?: number };
    if (err.status === 409) {
      toast.value = { kind: "error", message: "That combo was just taken — redrawing" };
      state.value = "IDLE";
      await spin();
      return;
    }
    toast.value = { kind: "error", message: e instanceof Error ? e.message : "Save failed" };
    state.value = "RESULT";
  }
}

function reset() {
  target.value = null;
  savedName.value = "";
  state.value = "IDLE";
}
</script>

<template>
  <main class="max-w-3xl mx-auto px-4 py-6">
    <h1 class="text-2xl sm:text-3xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-pot1 via-amber-500 to-pot3 bg-clip-text text-transparent">
      Pick your teams
    </h1>

    <p v-if="loadError" class="text-red-600">Failed to load teams: {{ loadError }}</p>

    <template v-else-if="teams.length">
      <PotReel
        :pot-number="1"
        :teams="potTeams(1)"
        :target-id="target?.pot1 ?? null"
        :duration="2.6"
        :trigger="spinTrigger"
      />
      <PotReel
        :pot-number="2"
        :teams="potTeams(2)"
        :target-id="target?.pot2 ?? null"
        :duration="2.9"
        :trigger="spinTrigger"
      />
      <PotReel
        :pot-number="3"
        :teams="potTeams(3)"
        :target-id="target?.pot3 ?? null"
        :duration="POT3_DURATION"
        :trigger="spinTrigger"
      />

      <div v-if="state === 'IDLE' || state === 'AWAITING_DRAW' || state === 'SPINNING'" class="mt-6">
        <SpinButton
          @click="spin"
          :disabled="state !== 'IDLE'"
          :label="state === 'SPINNING' ? 'Spinning…' : (state === 'AWAITING_DRAW' ? 'Drawing…' : 'Spin')"
        />
      </div>

      <ResultCard
        v-if="(state === 'RESULT' || state === 'SAVING') && targetTeams"
        :teams="targetTeams"
        :saving="state === 'SAVING'"
        @save="save"
        @cancel="reset"
      />

      <section v-if="state === 'SAVED' && targetTeams" class="mt-6 rounded-xl ring-1 ring-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm p-6 max-w-md mx-auto text-center">
        <p class="text-4xl mb-2">🎉</p>
        <p class="mb-3 text-slate-700"><strong class="text-slate-900">{{ savedName }}</strong> picked</p>
        <p class="text-pot1 font-bold text-lg">{{ targetTeams.pot1.name }}</p>
        <p class="text-pot2 font-bold text-lg">{{ targetTeams.pot2.name }}</p>
        <p class="text-pot3 font-bold text-lg">{{ targetTeams.pot3.name }} <span class="text-xs text-slate-500 font-medium">×2</span></p>
        <button class="mt-5 px-5 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-700 shadow-sm" @click="reset">Spin again</button>
      </section>

      <section v-if="state === 'EXHAUSTED'" class="mt-6 rounded-xl ring-1 ring-slate-300 bg-white shadow-sm p-6 max-w-md mx-auto text-center text-slate-700">
        <p>Every possible combination has been claimed (4,096 entries — bloody hell). No more spins.</p>
      </section>
    </template>

    <Toast v-if="toast" :kind="toast.kind" :message="toast.message" @dismiss="toast = null" />
  </main>
</template>
